import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorResponse } from '@klickdata/core/http';
import { Subject, combineLatest, of } from 'rxjs';
import { catchError, filter, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../token/auth.service';
import { OccasionAuthService } from './occasion-auth.service';

export interface OccasionLogin {
    state:
        | 'error'
        | 'authenticated_other'
        | 'authenticated_same'
        | 'none_authenticated'
        | 'guest_token_logout_required'
        | 'guest_token_no_logout_required';
    response?: ErrorResponse<any>;
    occasionId?: number;
    token?: string;
}

@Component({
    selector: 'app-occasion-auth',
    templateUrl: './occasion-auth.component.html',
    styleUrls: ['./occasion-auth.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [OccasionAuthService],
})
export class OccasionAuthComponent implements OnInit, OnDestroy {
    private destroy: Subject<boolean> = new Subject<boolean>();

    private isErrorResponse(response: unknown): response is ErrorResponse<unknown> {
        return !!response && typeof response === 'object' && 'error' in response;
    }

    private mapStatusCodeToOccasionLogin(statusCode: number | null | undefined, occasionId: number): OccasionLogin {
        console.log('statusCode', statusCode);
        switch (statusCode) {
            case 409:
                return { state: 'authenticated_other', occasionId };
            case 401:
                return { state: 'none_authenticated', occasionId };
            default:
                return { state: 'error', occasionId };
        }
    }

    constructor(
        protected auth: AuthService,
        protected occasionAuthService: OccasionAuthService,
        protected route: ActivatedRoute,
        protected router: Router
    ) {}

    ngOnInit() {
        // Case 1 return status_code: 200 'authorized_redirect: true'
        // OK – authorized, continue to occasion
        // >>> authenticated_same

        // Case 2 return status_code: 409, messages
        // Conflict – different user, FE must logout and redirect to login > authenticated_other
        // >>> authenticated_other

        // Case 3 return status_code:200 , 'token:limited_token', 'logout_required:true'
        // Ok - Guest token issued, FE must login and redirect to occasion > guest_token_or_authenticated
        // >>> guest_token_logout_required

        // Case 4 return status_code: 401 messages
        // Unauthorized – no session, FE must login and redirect to occasion > none_authenticated
        // >>> none_authenticated

        // Case 5 return status_code: 200 'token: 'limited_token','logout_required:false'
        // OK – guest token issued > guest_token
        // >>> guest_token_no_logout_required

        combineLatest([
            this.route.params.pipe(map((params) => params.occasionId)),
            this.route.queryParams.pipe(map((params) => params.token)),
        ])
            .pipe(
                switchMap(([occasionId, token]) =>
                    this.occasionAuthService.tokenLogin(occasionId, token).pipe(
                        filter((response) => !!response),
                        map((response) => {
                            if (this.isErrorResponse(response)) {
                                const statusCode = response.error?.statusCode ?? response.error?.status_code ?? null;
                                return this.mapStatusCodeToOccasionLogin(statusCode, occasionId);
                            }
                            return response?.data?.authorized_redirect
                                ? <OccasionLogin>{ state: 'authenticated_same', occasionId: occasionId }
                                : response?.data?.logout_required
                                ? <OccasionLogin>{
                                      state: 'guest_token_logout_required',
                                      response: response as ErrorResponse<any>,
                                      occasionId: occasionId,
                                      token: token,
                                  }
                                : <OccasionLogin>{
                                      state: 'guest_token_no_logout_required',
                                      response: response as ErrorResponse<any>,
                                      occasionId: occasionId,
                                      token: token,
                                  };
                        }),
                        catchError((err: any) => {
                            const statusCode: number | null =
                                err?.error?.statusCode ??
                                err?.error?.status_code ??
                                err?.statusCode ??
                                err?.status ??
                                null;
                            return of(this.mapStatusCodeToOccasionLogin(statusCode, occasionId));
                        })
                    )
                ),
                tap((occasionLogin: OccasionLogin) => {
                    const occURL = this.router.createUrlTree([`player/occasion`, occasionLogin.occasionId]).toString();
                    console.log('occasionLogin', occasionLogin);
                    switch (occasionLogin.state) {
                        case 'authenticated_same':
                            // Case 1
                            this.router.navigate([occURL]);
                            break;
                        case 'authenticated_other':
                            // Case 2
                            this.auth.handleOtherUserLogin(occURL);
                            break;
                        case 'guest_token_logout_required':
                            // Case 3
                            this.auth.handleGuestTokenLogoutRequired(occasionLogin.response, {
                                redirect_uri: occURL,
                            });
                            break;
                        case 'none_authenticated':
                            //case 4
                            this.router.navigate(['guest/login'], {
                                queryParams: {
                                    redirect_uri: occURL,
                                },
                            });
                            break;
                        case 'guest_token_no_logout_required':
                            // Case 5
                            this.auth.handleGuestTokenNoLogoutRequired(occasionLogin.response, {
                                redirect_uri: occURL,
                            });
                            break;
                        default:
                            this.router.navigate(['/unauthorised']);
                    }
                }),
                takeUntil(this.destroy)
            )
            .subscribe();
        // combineLatest([
        //     this.route.params.pipe(map((params) => params.occasionId)),
        //     this.route.queryParams.pipe(map((params) => params.token)),
        //     this.auth.check().pipe(first()),
        // ])
        //     .pipe(
        //         switchMap(([occasionId, token, authenticated]) =>
        //             this.occasionAuthService.tokenLogin(occasionId, token).pipe(
        //                 tap((response) => {
        //                     console.log('response', response);
        //                 }),
        //                 switchMap((response: ErrorResponse<any>) =>
        //                     response.error
        //                         ? of(<OccasionLogin>{ state: 'error', occasionId: occasionId })
        //                         : !authenticated
        //                         ? of(<OccasionLogin>{
        //                               state: 'none_authenticated',
        //                               response: response,
        //                               occasionId: occasionId,
        //                               token: token,
        //                           })
        //                         : this.auth.sameLogin(response.data.token).pipe(
        //                               first(),
        //                               map((sameUserLogin) =>
        //                                   sameUserLogin
        //                                       ? <OccasionLogin>{ state: 'authenticated_same', occasionId: occasionId }
        //                                       : <OccasionLogin>{
        //                                             state: 'authenticated_other',
        //                                             response: response,
        //                                             occasionId: occasionId,
        //                                         }
        //                               )
        //                           )
        //                 )
        //             )
        //         ),
        //         tap((occasionLogin: OccasionLogin) => {
        //             console.log('occasionLogin', occasionLogin);
        //             switch (occasionLogin.state) {
        //                 case 'error':
        //                     this.router.navigate(['guest/login'], {
        //                         queryParams: {
        //                             redirect_uri: this.router
        //                                 .createUrlTree([`player/occasion`, occasionLogin.occasionId])
        //                                 .toString(),
        //                         },
        //                     });
        //                     break;
        //                 case 'none_authenticated':
        //                 case 'authenticated_other':
        //                     this.auth.handleLogin(occasionLogin.response, {
        //                         redirect_uri: this.router
        //                             .createUrlTree([`player/occasion`, occasionLogin.occasionId])
        //                             .toString(),
        //                     });
        //                     break;
        //                 case 'authenticated_same':
        //                     this.router.navigate([`player/occasion`, occasionLogin.occasionId]);
        //                     break;
        //                 default:
        //                     this.router.navigate(['/unauthorised']);
        //             }
        //         }),
        //         takeUntil(this.destroy)
        //     )
        //     .subscribe();
    }

    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
